<template>
    <input-base
    :params="params"
    :rightButtonIcon="showInputValue ? 'eye-close' : 'eye-open'"
    :inputFocused="inputFocused"
    :inputValue="inputValue"
    @rightButtonClicked="toggleShowInputValue">
        <!-- Input slot -->
        <template v-slot:input>
            <input
            v-if="showInputValue"
            type="tel"
            autocomplete="new-password"
            v-mask="params.mask"
            :class="{'apply-dark-background-variant': params.darkBackgroundVariant, 'error-state': params.errorState }"
            :value="inputValue"
            :maxlength="params.maxLength"
            :placeholder="params.placeholder"
            inputmode="numeric"
            ref="showvalue"
			@keyup="checkIfReturnMobileClick"
            @focus="[emit('onFocus'), inputFocused = true]"
            @blur="blurHandler"
            @keyup.enter="emit('onEnter')"
            @input="handleInputChange">
            <input
            v-else
            type="password"
            autocomplete="new-password"
            v-mask="params.mask"
            :class="{'apply-dark-background-variant': params.darkBackgroundVariant, 'error-state': params.errorState }"
            :value="inputValue"
            :maxlength="params.maxLength"
            :placeholder="params.placeholder"
            inputmode="numeric"
            ref="hidevalue"
			@keyup="checkIfReturnMobileClick"
            @focus="[emit('onFocus'), inputFocused = true]"
            @blur="blurHandler"
            @keyup.enter="emit('onEnter')"
            @input="handleInputChange">
        </template>
    </input-base>
</template>

<script setup>
/* Components */
import InputBase from '@/components/generic/inputs/Base'

/* Vue Imports */
import { ref } from 'vue'

/* Emmitters */
const emit = defineEmits(['onFocus', 'onBlur', 'onEnter', 'input'])

/** props received
@params {
    - labelText {String}: label for textField
    - placeholder {String}: text for placholder
    - darkBackgroundVariant {Boolean}: property to apply specific colors for blue backgrounds
    - bottomMessage {String}: text to display in bottom elements
    - errorMessage {String}: text to display in bottom elements while in errorState
    - maxLength {Number}: maxlength for input
    - mandatory { Boolean } adds * to labelText
    - errorState {Boolean}: property to show if input has error or not
    - mask {Object, String}: mask for input
	- ourMask {Object}: use this mask if maska doesnt accept your regex, Object with:
		- regex {Regex}
		- validatorFn {Function} a function specific for validating regex
		- parseInputText {Function} a function that removes unwanted chars from your text in accord with regex
}
@inputValue {String, Number}: value to be displayed in input
*/
const props = defineProps({
    params: Object,
    inputValue: [String, Number]
})

const inputFocused = ref(false)
const showInputValue = ref(false)
const showvalue = ref('')
const hidevalue = ref('')

const checkIfReturnMobileClick = $event => {
	if ($event.which === 13) document.activeElement.blur()
}

const handleInputChange = $event => {
	if (props.params.ourMask) handleOurMask($event)
	else emit('input', props.params.mask ? $event.target.inputmask.unmaskedvalue() : $event.target.value)
}

const toggleShowInputValue = () => {
    showInputValue.value = !showInputValue.value
    if (!inputFocused.value) return

    setTimeout(() => {
        if (showInputValue.value) {
            showvalue.value.focus()
        } else {
            hidevalue.value.focus()
        }
    }, 0)
}

const blurHandler = $event => {
    emit('onBlur')
    if ($event.relatedTarget?.className.includes('button-quaternary')) return
    inputFocused.value = false
}

const handleOurMask = $event => {
	let text = $event.target.value

	if (props.params.ourMask.validatorFn && !props.params.ourMask.validatorFn($event.target.value, props.params.ourMask.regex)) {
		/* If we have parseInputText function we will need to parse our text before validating it */
		text = props.params.ourMask.parseInputText ? props.params.ourMask.parseInputText($event.target.value) : $event.target.value
		/* we will cut our text till it becomes valid */
		while (!props.params.ourMask.validatorFn(text, props.params.ourMask.regex) && text.length !== 0) {
			text = text.slice(0, -1)
		}
	} else if (props.params.ourMask.parseInputText) {
		text = props.params.ourMask.parseInputText($event.target.value)
	}
	$event.target.value = text
	emit('input', $event.target.value)
}
</script>

<style lang="scss" scoped>
input {
    width: 100%;
    height: 40px;

    @include H6-Regular;
    color: var(--secondary-dark-3);
}
</style>
